'use client';

import { useMutation } from '@apollo/client/react';
import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC, useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { ANIMATE_PHOTO } from '@/.graphql';
import { GenerateButton } from '@/src/components/GenerateButton';
import { Header } from '@/src/components/Header';
import { PromptArea } from '@/src/components/PromptArea';
import { useVitrinaPosition } from '@/src/hooks/useVitrinaPosition';
import {
  useStyleContext,
  useGTMContext,
  useProfileContext,
  useMobileContext,
} from '@/src/providers';

import { CloseAppButton } from '../_generate_image/CloseAppButton';
import { UploadButton } from '../_generate_image/UploadButton';

const styles = {
  portal: '!h-screen !max-h-screen !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  wrapper: 'shrink-0 bg-fill-menu pt-16 px-5',
  title: 'relative text-2xl font-bold mb-4 mt-2 text-center',
  info: 'flex flex-col gap-4 p-4 my-4 bg-fill-quinary rounded-xl',
  infoTitle: 'text-sm font-bold mb-3',
  infoList: 'list-disc pl-4',
  infoListItem: 'text-sm font-medium text-label-secondary',
  carouselContainer:
    'relative overflow-hidden w-full aspect-square mb-4 rounded-xl',
  carousel: 'flex duration-700 cursor-pointer',
  imageWrap:
    'relative shrink-0 flex flex-col items-center justify-end w-full aspect-square',
  image:
    'absolute w-full h-full top-0 left-0 object-cover object-top pointer-events-none',
  gradient: 'absolute top-0 left-0 right-0 bottom-0 bg-gradient-xl',
};

export const AnyImageToVideoPortal: FC = () => {
  const t = useTranslations('imageModal');
  const { genderMode, activeAspectRatio, prevPortal, setActiveAspectRatio } =
    useStyleContext();
  const sendGTM = useGTMContext();
  const { closePortal, openPortal, currentPortal } = usePortalContext();
  const profile = useProfileContext()!;
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const vitrinaPosition = useVitrinaPosition('photoByPhoto');
  const { isMobiles } = useMobileContext();

  const [animatePhoto, { loading }] = useMutation(ANIMATE_PHOTO, {
    context: {
      headers: {
        'apollo-require-preflight': true,
      },
    },
  });

  const [prompt, setPrompt] = useState<string>('');
  const [image, setImage] = useState<File | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const toastSuccessPhotoAddedIdRef = useRef<string | number | null>(null);

  const handleUploadButtonClick = () => {
    sendGTM({
      event: 'upload_photo',
      type: null,
      block: 'Анимировать фото',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: vitrinaPosition,
      source: 'Витрина',
      gender: genderMode,
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setImage(file);

    sendGTM({
      event: 'upload_photo_complete',
      type: null,
      block: 'Анимировать фото',
      category_name: null,
      name: null,
      category_position: null,
      vitrina_position: null,
      gender: genderMode,
      source: 'Витрина',
    });

    const reader = new FileReader();

    reader.onload = () => {
      const dataUrl = reader.result as string;

      setImageUrl(dataUrl);

      toastSuccessPhotoAddedIdRef.current = toast(
        <span className="whitespace-pre-line">{t('successPhotoAdded')}</span>,
        {
          autoClose: 1000,
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt="tick"
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        },
      );
    };

    reader.readAsDataURL(file);
  };

  const handleClickGenerate = async () => {
    try {
      setButtonDisabled(true);

      if (toastSuccessPhotoAddedIdRef.current) {
        toast.dismiss(toastSuccessPhotoAddedIdRef.current);
        toastSuccessPhotoAddedIdRef.current = null;
      }

      sendGTM({
        event: 'Generate',
        type: null,
        block: 'Анимировать фото',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: vitrinaPosition,
        source: 'Витрина',
        gender: genderMode,
      });

      const { data } = await animatePhoto({
        variables: {
          image: image,
          resolution: activeAspectRatio,
          prompt,
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      const queueTime = data.animatePhoto.queueTime;

      toast(
        <span>
          {t.rich('successMessage', {
            queueTime,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          })}
        </span>,
        {
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt="check"
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        },
      );

      sendGTM({
        event: 'Generation_complete',
        type: null,
        block: 'Анимировать фото',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: vitrinaPosition,
        source: 'Витрина',
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setImage(null);
        setImageUrl('');
      }, 5000);
    } catch (err) {
      const error = err as Error;

      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          closePortal();
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      } else {
        console.error(error);
      }

      sendGTM({
        event: 'Generation_error',
        type: null,
        block: 'Анимировать фото',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: vitrinaPosition,
        source: 'Витрина',
        gender: genderMode,
      });

      setTimeout(() => {
        setButtonDisabled(false);
        setImage(null);
        setImageUrl('');
      }, 5000);
    }
  };

  const handleClosePortal = () => {
    if (prevPortal === 'allTools') {
      closePortal();
      openPortal(PORTALS.AllTools);
    } else {
      closePortal();
    }
  };

  useEffect(() => {
    if (
      activeAspectRatio === '4:5' &&
      currentPortal === PORTALS.AnyImageToVideo
    ) {
      setActiveAspectRatio('1:1');
    }
  }, [activeAspectRatio, currentPortal, setActiveAspectRatio]);

  const disabled = buttonDisabled || loading;

  return (
    <PortalModal
      portalId={PORTALS.AnyImageToVideo}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <div className={styles.carouselContainer}>
              <div className={styles.carousel}>
                <div className={styles.imageWrap}>
                  {!image ? (
                    <>
                      <video
                        autoPlay
                        loop
                        muted
                        className={styles.image}
                        playsInline
                        webkit-playsinline="true"
                      >
                        <source src="/images/image_to_video_new.mp4" />
                      </video>
                      <div className={styles.gradient} />
                    </>
                  ) : (
                    <>
                      <Image
                        src="/images/inpaint-bg.webp"
                        width={826}
                        height={826}
                        quality={95}
                        priority
                        alt=""
                        className="pointer-events-none block absolute z-0 size-full translate-z-0 object-cover select-none"
                      />
                      {imageUrl && (
                        <Image
                          src={imageUrl}
                          width={826}
                          height={826}
                          quality={100}
                          priority
                          alt=""
                          className="pointer-events-none block size-full translate-z-0 absolute z-1 object-contain select-none"
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {!image ? (
              <div className={styles.info}>
                <div>
                  <div className={styles.infoTitle}>{t('info.title')}</div>
                  <ul className={styles.infoList}>
                    <li className={styles.infoListItem}>
                      {t('info.videoText')}
                    </li>
                  </ul>
                </div>
                <div>
                  <div className={styles.infoTitle}>
                    {t('info.howItWorks.title')}
                  </div>
                  <ul className={styles.infoList}>
                    <li className={styles.infoListItem}>
                      {t('info.howItWorks.imageToVideoText')}
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-4 mb-4">
                <PromptArea
                  changePrompt={setPrompt}
                  prompt={prompt}
                  placeholder={t('promptImageToVideoPlaceholder')}
                  maxLength={2500}
                  className="!bg-fill-quinary !text-sm !font-normal placeholder:!leading-6 !rounded-xl !min-h-[136px] !h-[136px] !border-yellow"
                />

                {/* <AspectRatio /> */}
              </div>
            )}

            {profile?.isAcitveStartFunnel ? (
              <CloseAppButton
                text={t('buttonPay')}
                keybord="paymentDownSale4"
              />
            ) : !image ? (
              <UploadButton
                disabled={disabled}
                isMultiple={false}
                buttonText={t('buttonUploadPhoto')}
                onChange={handleFileChange}
                onClick={handleUploadButtonClick}
              />
            ) : (
              <GenerateButton
                disabled={disabled || !prompt}
                onClick={handleClickGenerate}
                text={t.rich('buttonImageToVideoMode', {
                  priceGeneration: '12',
                  b: (chunks: React.ReactNode) => (
                    <b className="font-bold">{chunks}</b>
                  ),
                })}
                price="12"
              />
            )}
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
